import React, { useEffect, useCallback } from "react";
import axios from "axios";
import { useForm, Controller, FormProvider, useFormContext } from "react-hook-form";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import swal from "sweetalert";
import Card from "../../Components/Cards/Card";
import SubmitButton from "../../Components/Buttons/SubmitButton";
import DateTimeInput from "../../Components/Inputs/DateTimeInput";
import ReactSelectInput from "../../Components/Inputs/ReactSelectInput";
import TextInput from "../../Components/Inputs/TextInput";
import { useBrands } from "../../Hooks/useBrands";
import { useProductSearch } from "../../Hooks/useProductSearch";
import { useCategories } from "../../Hooks/useCategories";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const ACTIVE_OPTIONS = [
  { id: 1, name: "Active" },
  { id: 0, name: "Inactive" },
];

const StatusSelectInput = ({ label, name, control, errorMessage, options }) => {
  const { setValue, watch } = useFormContext();
  const value = watch(name);

  const selectedOption = options.find((option) => option.id === value) || null;

  const handleChange = (numericValue) => {
    setValue(name, numericValue, { shouldValidate: true });
  };

  return (
    <ReactSelectInput
      label={label}
      name={name}
      control={control}
      errorMessage={errorMessage}
      options={options}
      value={selectedOption}
      callback={handleChange}
    />
  );
};

export default function EditCashbackRules() {
  const methods = useForm({
    defaultValues: {
      cashback_active: 1,
    },
  });
  const { reset, handleSubmit, control, register, getValues, watch, setValue } = methods;
  const [errors, setErrors] = React.useState({});
  const { filteredProducts, searchProduct, updateFilteredProducts } = useProductSearch();
  const { brands } = useBrands();
  const { flattenedCategories } = useCategories();

  const selectedCategories = watch("categories") || [];
  const selectedBrands = watch("brands") || [];
  const selectedProducts = watch("products") || [];
  const selectedBanks = watch("banks") || [];

  const banks = [
    { id: 1, name: "TBC" },  
    { id: 2, name: "BOG" },
  ]

  const deduplicateById = useCallback((items) => {
    const seen = new Set();
    return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, []);

  const removeItem = useCallback(
    (fieldName, itemId) => {
      const currentItems = getValues(fieldName) || [];
      const updatedItems = currentItems.filter((item) => item.id !== itemId);
      setValue(fieldName, updatedItems, { shouldValidate: true });
    },
    [getValues, setValue]
  );

  const handleProductSearch = useCallback(
    async (inputValue) => {
      const currentProducts = getValues("products") || [];
      const result = await searchProduct(inputValue, currentProducts);
      return result;
    },
    [getValues, searchProduct]
  );

  useEffect(() => {
    axios
      .get("cashbacks")
      .then(async (res) => {
        if (res.data.status === "success" && res.data.cashback_rules) {
          const rule = res.data.cashback_rules;

          const productIds = rule.products || [];
          const initialProducts = productIds.map((id) => ({
            id,
            name: `Product ${id}`,
          }));

          const formData = {
            price_from: rule.price_from || "",
            price_to: rule.price_to || "",
            cashback_date_from: rule.cashback_date_from || null,
            cashback_date_to: rule.cashback_date_to || null,
            cashback_active: rule.cashback_active ? 1 : 0,
            categories: deduplicateById(
              (rule.categories || []).map(
                (id) =>
                  flattenedCategories.find((c) => c.id === id) || {
                    id,
                    name: `Category ${id}`,
                  }
              )
            ),
            brands: deduplicateById(
              (rule.brands || []).map(
                (id) =>
                  brands.find((b) => b.id === id) || {
                    id,
                    name: `Brand ${id}`,
                  }
              )
            ),
            banks: deduplicateById(
              (rule.banks || []).map( 
                (id) =>
                  banks.find((b) => b.id === id) || {
                    id,
                    name: `Bank ${id}`,
                  }
              )
            ),
            products: deduplicateById(initialProducts),
            popup_active: rule.active_popup ? 1 : 0,
            popup_text: rule.popup_text || "",
            banner_active: rule.active_banner ? 1 : 0,
            banner_text: rule.banner_text || "",
          };

          reset(formData);
        }
      })
      .catch((error) => {
        console.error("Failed to load cashback rules:", error);
      });
  }, [flattenedCategories, brands, reset, deduplicateById]);

  useEffect(() => {
    const selectedProducts = getValues("products") || [];
    const missingIds = selectedProducts
      .filter((p) => p.name.startsWith("Product"))
      .map((p) => p.id);

    if (missingIds.length > 0) {
      axios
        .get("search-products-list", {
          params: { product_ids: missingIds },
        })
        .then((response) => {
          const fetchedProducts = response.data?.products || [];
          updateFilteredProducts(fetchedProducts);
          const updatedProducts = selectedProducts.map((product) => {
            const fetchedProduct = fetchedProducts.find(
              (p) => p.id === product.id
            );
            return fetchedProduct || product;
          });
          setValue("products", deduplicateById(updatedProducts), {
            shouldValidate: true,
          });
        })
        .catch((error) => {
          console.error("Error fetching missing products:", error);
        });
    }
  }, [getValues, setValue, updateFilteredProducts, deduplicateById]);

  const saveRule = (data) => {

    const payload = {
      price_from: data.price_from,
      price_to: data.price_to,
      cashback_date_from: data.cashback_date_from,
      cashback_date_to: data.cashback_date_to,
      cashback_active: data.cashback_active === 1,
      categories: data.categories.map((item) => item.id),
      brands: data.brands.map((item) => item.id),
      products: data.products.map((item) => item.id),
      active_popup: data.popup_active === 1,
      popup_text: data.popup_text,
      active_banner: data.banner_active === 1,
      banner_text: data.banner_text,
      banks: data.banks.map((item) => item.id),
    };

    axios
      .put("cashbacks", payload)
      .then((res) => {
        if (res.status === 200) {
          swal("Cashback Rules updated successfully!", { icon: "success" });
          setErrors({});
        }
      })
      .catch((error) => {
        if (error.response) {
          setErrors(error.response.data.errors || {});
          swal("Error", error.response.data.message || "Failed to save cashback rules", "error");
        }
      });
  };

  const MultiSelectInput = ({ label, name, values, selectedItems, setValue }) => (
    <div className="col-xxl-12 col-lg-12 mb-5">
      <label>{label}</label>
      <div className="mb-2 d-flex gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => setValue(name, values, { shouldValidate: true })}
        >
          Select All
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setValue(name, [], { shouldValidate: true })}
        >
          Deselect All
        </button>
      </div>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            isMulti
            options={values}
            value={field.value}
            onChange={(selected) => field.onChange(deduplicateById(selected || []))}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            placeholder={`Choose ${label}`}
          />
        )}
      />
      <div className="mt-2">
        {selectedItems.map((item) => (
          <span key={item.id} className="badge bg-success me-2 mb-2">
            {item.name || "Unnamed"}
            <button
              type="button"
              className="btn-close btn-close-white ms-2"
              aria-label="Remove"
              onClick={() => removeItem(name, item.id)}
            />
          </span>
        ))}
      </div>
    </div>
  );


  const AsyncMultiSelectInput = ({ label, name, search, selectedItems }) => (
    <div className="col-xxl-12 col-lg-12 mb-5">
      <label>{label}</label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <AsyncSelect
            key={filteredProducts.length}
            isMulti
            cacheOptions
            defaultOptions={filteredProducts}
            loadOptions={search}
            value={field.value}
            onChange={(selected) => field.onChange(deduplicateById(selected || []))}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            placeholder={`Choose ${label}`}
          />
        )}
      />
      <div className="mt-2">
        {selectedItems.map((item) => (
          <span key={item.id} className="badge bg-success me-2 mb-2">
            {item.name || "Unnamed"}
            <button
              type="button"
              className="btn-close btn-close-white ms-2"
              aria-label="Remove"
              onClick={() => removeItem(name, item.id)}
            />
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <FormProvider {...methods}>
      <Card title="Edit Cashback Rules">
        <form className="form w-100 row" onSubmit={handleSubmit(saveRule)}>
          <MultiSelectInput
            label="Categories"
            name="categories"
            values={flattenedCategories}
            selectedItems={selectedCategories}
            setValue={setValue}
          />

          <MultiSelectInput
            label="Brands"  
            name="brands"
            values={brands}
            selectedItems={selectedBrands}
            setValue={setValue}
          />

          <MultiSelectInput
            label="Banks"
            name="banks"
            values={banks}
            selectedItems={selectedBanks}
            setValue={setValue}
          />

          <AsyncMultiSelectInput
            label="Products"
            name="products"
            search={handleProductSearch}
            selectedItems={selectedProducts}
          />

          <TextInput
            label="Cashback Price From"
            name="price_from"
            parentClasses="col-md-6"
            register={register}
            errorMessage={errors?.price_from}
          />
          <TextInput
            label="Cashback Price To"
            name="price_to"
            parentClasses="col-md-6"
            register={register}
            errorMessage={errors?.price_to}
          />
          <DateTimeInput
            label="Cashback Date from"
            name="cashback_date_from"
            register={register}
            errorMessage={errors?.date_from}
            control={control}
          />
          <DateTimeInput
            label="Cashback Date to"
            name="cashback_date_to"
            register={register}
            errorMessage={errors?.date_to}
            control={control}
          />
          <StatusSelectInput
            label="Status"
            name="cashback_active"
            control={control}
            errorMessage={errors?.cashback_active}
            options={ACTIVE_OPTIONS}
          />


          <hr className="separator" />

          <StatusSelectInput
            label="Popup Status"
            name="popup_active"
            control={control}
            errorMessage={errors?.active_popup}
            options={ACTIVE_OPTIONS}
          />

          <div className="col-md-12 mb-5">
            <label className="form-label">Popup Text</label>
            <div
              id="kt_ecommerce_add_popup_text_description"
              name="kt_ecommerce_add_popup_text_description"
              className="min-h-200px mb-2"
            >
              <Controller
                name="popup_text"
                control={control}
                render={({ field }) => (
                  <CKEditor
                    editor={ClassicEditor}
                    data={field.value ?? ""}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      field.onChange(data);
                    }}
                  />
                )}
              />
            </div>
            {errors?.popup_text && (
              <div className="fs-7 mb-7 text-danger">{errors.popup_text}</div>
            )}
          </div>

          <hr className="separator" />

          <StatusSelectInput
            label="Banner Status"
            name="banner_active"
            control={control}
            errorMessage={errors?.active_banner}
            options={ACTIVE_OPTIONS}
          />

          <div className="col-md-12 mb-5">
            <label className="form-label">Banner Text</label>
            <div
              id="kt_ecommerce_add_banner_text_description"
              name="kt_ecommerce_add_banner_text_description"
              className="min-h-200px mb-2"
            >
              <Controller
                name="banner_text"
                control={control}
                render={({ field }) => (
                  <CKEditor
                    editor={ClassicEditor}
                    data={field.value ?? ""}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      field.onChange(data);
                    }}
                  />
                )}
              />
            </div>
            {errors?.banner_text && (
              <div className="fs-7 mb-7 text-danger">{errors.banner_text}</div>
            )}
          </div>

          <SubmitButton title="Save" classes="w-auto d-inline-block mt-4" />
        </form>
      </Card>
    </FormProvider>
  );
}
